// Catalogue management page — global brand/model CRUD for car and technology types,
// plus a flat color catalogue (global, no type scope).
// Master-detail layout: brands on the left (selectable), the selected brand's models on the right.
// UX goals: actions always visible (no hover-only), accurate model counts where deletes happen,
// rapid keyboard-first add (clear + refocus), and honest reversible-delete copy.

import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search, Pencil, Trash2, Plus, Check, X, Package, ChevronRight, Loader2, Palette } from "lucide-react";
import { useDebounce } from "@/shared/lib/useDebounce";
import { useToast } from "@/shared/ui/Toast";
import { ConfirmModal } from "@/shared/ui/ConfirmModal";
import { Input } from "@/shared/ui/Input";
import type { LoanCatalogueType, CatalogueBrand, CatalogueModel, CatalogueColor } from "./types";
import {
  useCatalogueBrands,
  useCatalogueModels,
  useCreateBrand,
  useCreateModel,
  useUpdateBrand,
  useDeleteBrand,
  useUpdateModel,
  useDeleteModel,
  useColors,
  useCreateColor,
  useUpdateColor,
  useDeleteColor,
} from "./hooks";

// The active tab — car/technology use master-detail; colors use a flat list panel.
type CatalogueTab = "car" | "technology" | "colors";

// ── Segmented type toggle — 3 tabs: car | technology | colors ─────────────────

function TypeToggle({
  value,
  onChange,
}: {
  value: CatalogueTab;
  onChange: (t: CatalogueTab) => void;
}) {
  const { t } = useTranslation();
  const options: Array<{ key: CatalogueTab; label: string }> = [
    { key: "car", label: t("catalogue.car") },
    { key: "technology", label: t("catalogue.technology") },
    { key: "colors", label: t("catalogue.colors") },
  ];
  return (
    <div className="inline-flex h-[38px] rounded-[9px] border border-grey-300 bg-white p-0.5">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          aria-pressed={value === opt.key}
          className={`flex h-full items-center rounded-[7px] px-4 text-[13.5px] font-medium transition-colors ${
            value === opt.key
              ? "bg-green text-white shadow-sm"
              : "bg-transparent text-grey-600 hover:text-grey-800"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Inline rename input (shared by brand header + model rows) ──────────────────

function InlineRename({
  initialValue,
  onSave,
  onCancel,
  busy,
}: {
  initialValue: string;
  onSave: (name: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") onSave(value.trim());
    if (e.key === "Escape") onCancel();
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {/* Inline list-item rename field — default size, custom compact height + green active border. */}
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKey}
        disabled={busy}
        className="h-[32px] min-w-0 rounded-[7px] border-green px-2.5 text-[13.5px] focus-visible:border-green focus-visible:ring-green"
      />
      <button
        type="button"
        onClick={() => onSave(value.trim())}
        disabled={busy || !value.trim()}
        aria-label={t("catalogue.save")}
        className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-[7px] bg-green text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-50"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.5} />}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        aria-label={t("catalogue.cancel")}
        className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-[7px] border border-grey-300 bg-white text-grey-500 transition active:scale-95 hover:bg-grey-50 disabled:opacity-50"
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ── Rapid inline add (clears + refocuses on success, so you can keep typing) ───

function InlineAdd({
  placeholder,
  onAdd,
  busy,
}: {
  placeholder: string;
  // Returns true on success so the field clears and refocuses for the next entry.
  onAdd: (name: string) => Promise<boolean>;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  async function submit() {
    const name = value.trim();
    if (!name || busy) return;
    const ok = await onAdd(name);
    if (ok) {
      setValue("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative min-w-0 flex-1">
        {/* Leading-icon: pl-8 preserves room for the Plus icon at left-2.5. */}
        <Plus
          size={15}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-grey-400"
        />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder={placeholder}
          disabled={busy}
          className="h-9 rounded-[9px] pl-8 pr-3 text-[13.5px]"
        />
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={busy || !value.trim()}
        className="flex h-9 flex-none items-center gap-1 rounded-[9px] bg-green px-3.5 text-[12.5px] font-semibold text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-50"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} strokeWidth={2.5} />}
        {t("catalogue.add")}
      </button>
    </div>
  );
}

// ── Left pane: selectable brand list ──────────────────────────────────────────

function BrandList({
  type,
  brands,
  filtered,
  isLoading,
  isError,
  searching,
  selectedId,
  onSelect,
  onAddBrand,
  addBusy,
}: {
  type: LoanCatalogueType;
  brands: CatalogueBrand[] | undefined;
  filtered: CatalogueBrand[];
  isLoading: boolean;
  isError: boolean;
  searching: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onAddBrand: (name: string) => Promise<boolean>;
  addBusy: boolean;
}) {
  const { t } = useTranslation();
  const hasBrands = (brands?.length ?? 0) > 0;

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-card border border-grey-200 bg-white shadow-card">
      <header className="flex flex-none items-center justify-between border-b border-grey-200 px-4 py-3">
        <h2 className="text-[13.5px] font-semibold text-grey-800">{t("catalogue.colBrand")}</h2>
        <span className="rounded-full bg-grey-100 px-2 py-0.5 text-[11.5px] font-medium text-grey-600">
          {brands ? t("catalogue.brandsCount", { count: brands.length }) : "—"}
        </span>
      </header>

      <div className="max-h-[340px] min-h-0 flex-1 overflow-y-auto md:max-h-none">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border-b border-grey-100 px-4 py-3">
              <div className="h-3.5 w-28 animate-pulse rounded bg-grey-100" />
            </div>
          ))}

        {isError && (
          <p className="px-4 py-6 text-center text-[13px] text-grey-500">{t("catalogue.loadError")}</p>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div className="px-4 py-10 text-center">
            <p className="text-[13.5px] font-medium text-grey-700">
              {searching ? t("catalogue.noResults") : t("catalogue.empty")}
            </p>
            {!searching && !hasBrands && (
              <p className="mt-1 text-[12.5px] text-grey-500">{t("catalogue.emptyHint")}</p>
            )}
          </div>
        )}

        {!isLoading &&
          !isError &&
          filtered.map((brand) => {
            const active = brand.id === selectedId;
            return (
              <button
                key={brand.id}
                type="button"
                onClick={() => onSelect(brand.id)}
                aria-current={active}
                className={`flex w-full items-center justify-between gap-2 border-b border-grey-100 border-l-2 px-4 py-2.5 text-left transition-colors ${
                  active
                    ? "border-l-green bg-green-bg text-green-deep"
                    : "border-l-transparent text-grey-700 hover:bg-grey-50"
                }`}
              >
                <span
                  className={`truncate text-[13.5px] ${active ? "font-semibold" : "font-medium"}`}
                >
                  {brand.name}
                </span>
                <ChevronRight
                  size={15}
                  className={active ? "flex-none text-green" : "flex-none text-grey-300"}
                />
              </button>
            );
          })}
      </div>

      <div className="flex-none border-t border-grey-200 bg-grey-50 p-3">
        <InlineAdd placeholder={t("catalogue.addBrandPlaceholder")} busy={addBusy} onAdd={onAddBrand} />
        <p className="sr-only">{type}</p>
      </div>
    </aside>
  );
}

// ── Right pane: selected brand's models + brand actions ────────────────────────

function ModelDetail({
  type,
  brand,
  onBrandDeleted,
}: {
  type: LoanCatalogueType;
  brand: CatalogueBrand;
  onBrandDeleted: () => void;
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { data: models, isLoading, isError } = useCatalogueModels(type, brand.id);
  const modelCount = models?.length ?? 0;

  const [renamingBrand, setRenamingBrand] = useState(false);
  const [deleteBrandOpen, setDeleteBrandOpen] = useState(false);
  const [renamingModelId, setRenamingModelId] = useState<number | null>(null);
  const [deleteModel, setDeleteModel] = useState<CatalogueModel | null>(null);

  const updateBrand = useUpdateBrand();
  const deleteBrand = useDeleteBrand();
  const createModel = useCreateModel();
  const updateModel = useUpdateModel();
  const deleteModelMut = useDeleteModel();

  // Reset row-level editing when switching brands.
  useEffect(() => {
    setRenamingBrand(false);
    setDeleteBrandOpen(false);
    setRenamingModelId(null);
    setDeleteModel(null);
  }, [brand.id]);

  function handleRenameBrand(name: string) {
    if (!name) return;
    updateBrand.mutate(
      { type, id: brand.id, name },
      {
        onSuccess: () => {
          setRenamingBrand(false);
          showToast(t("catalogue.saved"));
        },
        onError: () => showToast(t("catalogue.error")),
      },
    );
  }

  function handleDeleteBrand() {
    deleteBrand.mutate(
      { type, id: brand.id },
      {
        onSuccess: () => {
          setDeleteBrandOpen(false);
          showToast(t("catalogue.deleted"));
          onBrandDeleted();
        },
        onError: () => {
          setDeleteBrandOpen(false);
          showToast(t("catalogue.error"));
        },
      },
    );
  }

  async function handleAddModel(name: string): Promise<boolean> {
    try {
      await createModel.mutateAsync({ type, brandId: brand.id, name });
      showToast(t("catalogue.saved"));
      return true;
    } catch {
      showToast(t("catalogue.error"));
      return false;
    }
  }

  function handleRenameModel(model: CatalogueModel, name: string) {
    if (!name) return;
    updateModel.mutate(
      { type, id: model.id, brandId: brand.id, name },
      {
        onSuccess: () => {
          setRenamingModelId(null);
          showToast(t("catalogue.saved"));
        },
        onError: () => showToast(t("catalogue.error")),
      },
    );
  }

  function handleDeleteModel() {
    if (!deleteModel) return;
    deleteModelMut.mutate(
      { type, id: deleteModel.id, brandId: brand.id },
      {
        onSuccess: () => {
          setDeleteModel(null);
          showToast(t("catalogue.deleted"));
        },
        onError: () => {
          setDeleteModel(null);
          showToast(t("catalogue.error"));
        },
      },
    );
  }

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-card border border-grey-200 bg-white shadow-card">
      {/* Brand header */}
      <header className="flex flex-none items-center justify-between gap-2 border-b border-grey-200 px-5 py-3">
        {renamingBrand ? (
          <div className="w-full max-w-[320px]">
            <InlineRename
              initialValue={brand.name}
              busy={updateBrand.isPending}
              onSave={handleRenameBrand}
              onCancel={() => setRenamingBrand(false)}
            />
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-2.5">
            <h2 className="truncate text-[15px] font-semibold text-grey-900">{brand.name}</h2>
            <span className="flex-none rounded-full bg-grey-100 px-2 py-0.5 text-[11.5px] font-medium text-grey-600">
              {isLoading ? "…" : t("catalogue.modelsCount", { count: modelCount })}
            </span>
          </div>
        )}

        {!renamingBrand && (
          <div className="flex flex-none items-center gap-1">
            <button
              type="button"
              onClick={() => setRenamingBrand(true)}
              aria-label={t("catalogue.rename")}
              title={t("catalogue.rename")}
              className="flex h-8 w-8 items-center justify-center rounded-[7px] text-grey-400 transition active:scale-95 hover:bg-grey-100 hover:text-grey-700"
            >
              <Pencil size={15} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => setDeleteBrandOpen(true)}
              aria-label={t("catalogue.delete")}
              title={t("catalogue.delete")}
              className="flex h-8 w-8 items-center justify-center rounded-[7px] text-grey-400 transition active:scale-95 hover:bg-red-bg hover:text-warning"
            >
              <Trash2 size={15} strokeWidth={2} />
            </button>
          </div>
        )}
      </header>

      {/* Model list */}
      <div className="max-h-[460px] min-h-0 flex-1 overflow-y-auto md:max-h-none">
        {isLoading && (
          <div className="space-y-3 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-3.5 w-44 animate-pulse rounded bg-grey-100" />
            ))}
          </div>
        )}

        {isError && (
          <p className="px-5 py-6 text-center text-[13px] text-grey-500">{t("catalogue.loadError")}</p>
        )}

        {!isLoading && !isError && modelCount === 0 && (
          <div className="px-5 py-12 text-center">
            <p className="text-[14px] font-medium text-grey-700">{t("catalogue.noModels")}</p>
            <p className="mt-1 text-[12.5px] text-grey-500">{t("catalogue.noModelsHint")}</p>
          </div>
        )}

        {!isLoading &&
          !isError &&
          models?.map((model) => (
            <div
              key={model.id}
              className="flex items-center justify-between gap-3 border-b border-grey-100 px-5 py-2.5"
            >
              {renamingModelId === model.id ? (
                <div className="w-full max-w-[320px]">
                  <InlineRename
                    initialValue={model.name}
                    busy={updateModel.isPending}
                    onSave={(name) => handleRenameModel(model, name)}
                    onCancel={() => setRenamingModelId(null)}
                  />
                </div>
              ) : (
                <>
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-[13.5px] text-grey-800">{model.name}</span>
                    <span className="truncate text-[11.5px] text-grey-400">{model.canonical}</span>
                  </div>
                  {/* Actions are always visible — discoverable + touch-friendly. */}
                  <div className="flex flex-none items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setRenamingModelId(model.id)}
                      aria-label={t("catalogue.rename")}
                      title={t("catalogue.rename")}
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] text-grey-300 transition active:scale-95 hover:bg-grey-100 hover:text-grey-700"
                    >
                      <Pencil size={13.5} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteModel(model)}
                      aria-label={t("catalogue.delete")}
                      title={t("catalogue.delete")}
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] text-grey-300 transition active:scale-95 hover:bg-red-bg hover:text-warning"
                    >
                      <Trash2 size={13.5} strokeWidth={2} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
      </div>

      {/* Add model */}
      <div className="flex-none border-t border-grey-200 bg-grey-50 p-3">
        <InlineAdd
          placeholder={t("catalogue.addModelPlaceholder")}
          busy={createModel.isPending}
          onAdd={handleAddModel}
        />
      </div>

      <ConfirmModal
        open={deleteBrandOpen}
        title={t("catalogue.deleteBrandTitle")}
        message={t("catalogue.deleteBrandHint", { count: modelCount })}
        confirmLabel={t("catalogue.confirm")}
        cancelLabel={t("catalogue.cancel")}
        tone="warning"
        busy={deleteBrand.isPending}
        onConfirm={handleDeleteBrand}
        onCancel={() => setDeleteBrandOpen(false)}
      />
      <ConfirmModal
        open={deleteModel !== null}
        title={t("catalogue.deleteModelTitle")}
        message={t("catalogue.deleteModelHint")}
        confirmLabel={t("catalogue.confirm")}
        cancelLabel={t("catalogue.cancel")}
        tone="warning"
        busy={deleteModelMut.isPending}
        onConfirm={handleDeleteModel}
        onCancel={() => setDeleteModel(null)}
      />
    </section>
  );
}

// ── Empty right pane (no brand selected) ───────────────────────────────────────

function NoBrandSelected() {
  const { t } = useTranslation();
  return (
    <section className="flex min-h-[360px] flex-col items-center justify-center rounded-card border border-dashed border-grey-300 bg-grey-50 px-6 py-12 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-grey-400 shadow-card">
        <Package size={22} strokeWidth={1.75} />
      </div>
      <p className="text-[14px] font-semibold text-grey-800">{t("catalogue.selectBrandTitle")}</p>
      <p className="mt-1 max-w-[260px] text-[12.5px] leading-relaxed text-grey-500">
        {t("catalogue.selectBrand")}
      </p>
    </section>
  );
}

// ── Color bilingual inline-rename (uz + ru name fields) ──────────────────────

function ColorInlineRename({
  initialValue,
  initialValueRu,
  onSave,
  onCancel,
  busy,
}: {
  initialValue: string;
  initialValueRu?: string | null;
  onSave: (name: string, name_ru: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [valueRu, setValueRu] = useState(initialValueRu ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") onSave(value.trim(), valueRu.trim());
    if (e.key === "Escape") onCancel();
  }

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {/* UZ name — green border matches the active-rename visual cue. */}
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKey}
        disabled={busy}
        placeholder={t("catalogue.addColorPlaceholder")}
        className="h-[32px] min-w-0 rounded-[7px] border-green px-2.5 text-[13.5px] focus-visible:border-green focus-visible:ring-green"
      />
      {/* RU name — optional secondary field, standard grey border. */}
      <Input
        type="text"
        value={valueRu}
        onChange={(e) => setValueRu(e.target.value)}
        onKeyDown={handleKey}
        disabled={busy}
        placeholder={t("catalogue.addColorNameRuPlaceholder")}
        className="h-[32px] min-w-0 rounded-[7px] px-2.5 text-[13.5px]"
      />
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onSave(value.trim(), valueRu.trim())}
          disabled={busy || !value.trim()}
          aria-label={t("catalogue.save")}
          className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-[7px] bg-green text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.5} />}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          aria-label={t("catalogue.cancel")}
          className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-[7px] border border-grey-300 bg-white text-grey-500 transition active:scale-95 hover:bg-grey-50 disabled:opacity-50"
        >
          <X size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// ── Color bilingual inline-add (uz name + optional ru name) ───────────────────

function ColorInlineAdd({
  busy,
  onAdd,
}: {
  busy: boolean;
  // Returns true on success so the fields clear and refocus.
  onAdd: (name: string, name_ru: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [valueRu, setValueRu] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  async function submit() {
    const name = value.trim();
    if (!name || busy) return;
    const ok = await onAdd(name, valueRu.trim());
    if (ok) {
      setValue("");
      setValueRu("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {/* Leading-icon UZ name field: pl-8 preserves room for the Plus icon at left-2.5. */}
        <div className="relative min-w-0 flex-1">
          <Plus
            size={15}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-grey-400"
          />
          <Input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder={t("catalogue.addColorPlaceholder")}
            disabled={busy}
            className="h-9 rounded-[9px] pl-8 pr-3 text-[13.5px]"
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !value.trim()}
          className="flex h-9 flex-none items-center gap-1 rounded-[9px] bg-green px-3.5 text-[12.5px] font-semibold text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} strokeWidth={2.5} />}
          {t("catalogue.add")}
        </button>
      </div>
      {/* Optional RU name field — no icon, standard padding. */}
      <div className="relative min-w-0">
        <Input
          type="text"
          value={valueRu}
          onChange={(e) => setValueRu(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder={t("catalogue.addColorNameRuPlaceholder")}
          disabled={busy}
          className="h-9 rounded-[9px] px-3 text-[13.5px]"
        />
      </div>
    </div>
  );
}

// ── Colors panel — flat list (no master-detail) ────────────────────────────────

function ColorPanel() {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const { data: colors, isLoading, isError } = useColors();
  const createColor = useCreateColor();
  const updateColor = useUpdateColor();
  const deleteColor = useDeleteColor();

  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CatalogueColor | null>(null);

  async function handleAdd(name: string, name_ru: string): Promise<boolean> {
    try {
      await createColor.mutateAsync({ name, name_ru: name_ru || undefined });
      showToast(t("catalogue.saved"));
      return true;
    } catch {
      showToast(t("catalogue.error"));
      return false;
    }
  }

  function handleRename(color: CatalogueColor, name: string, name_ru: string) {
    if (!name) return;
    updateColor.mutate(
      { id: color.id, name, name_ru: name_ru || undefined },
      {
        onSuccess: () => {
          setRenamingId(null);
          showToast(t("catalogue.saved"));
        },
        onError: () => showToast(t("catalogue.error")),
      },
    );
  }

  function handleDelete() {
    if (!deleteTarget) return;
    deleteColor.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast(t("catalogue.deleted"));
      },
      onError: () => {
        setDeleteTarget(null);
        showToast(t("catalogue.error"));
      },
    });
  }

  const count = colors?.length ?? 0;

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-card border border-grey-200 bg-white shadow-card">
      {/* Header */}
      <header className="flex flex-none items-center justify-between border-b border-grey-200 px-4 py-3">
        <h2 className="text-[13.5px] font-semibold text-grey-800">{t("catalogue.colors")}</h2>
        <span className="rounded-full bg-grey-100 px-2 py-0.5 text-[11.5px] font-medium text-grey-600">
          {colors ? t("catalogue.colorsCount", { count }) : "—"}
        </span>
      </header>

      {/* Color list */}
      <div className="max-h-[460px] min-h-0 flex-1 overflow-y-auto md:max-h-none">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border-b border-grey-100 px-4 py-3">
              <div className="h-3.5 w-28 animate-pulse rounded bg-grey-100" />
            </div>
          ))}

        {isError && (
          <p className="px-4 py-6 text-center text-[13px] text-grey-500">
            {t("catalogue.loadError")}
          </p>
        )}

        {!isLoading && !isError && count === 0 && (
          <div className="px-4 py-12 text-center">
            <div className="mb-3 flex h-12 w-12 mx-auto items-center justify-center rounded-full bg-grey-50 text-grey-400 shadow-card">
              <Palette size={22} strokeWidth={1.75} />
            </div>
            <p className="text-[13.5px] font-medium text-grey-700">{t("catalogue.noColors")}</p>
            <p className="mt-1 text-[12.5px] text-grey-500">{t("catalogue.noColorsHint")}</p>
          </div>
        )}

        {!isLoading &&
          !isError &&
          colors?.map((color) => (
            <div
              key={color.id}
              className="flex items-center justify-between gap-3 border-b border-grey-100 px-4 py-2.5"
            >
              {renamingId === color.id ? (
                <div className="w-full max-w-[320px]">
                  <ColorInlineRename
                    initialValue={color.name}
                    initialValueRu={color.name_ru}
                    busy={updateColor.isPending}
                    onSave={(name, name_ru) => handleRename(color, name, name_ru)}
                    onCancel={() => setRenamingId(null)}
                  />
                </div>
              ) : (
                <>
                  {/* Primary name + secondary muted ru name (like the model rows). */}
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-[13.5px] text-grey-800">{color.name}</span>
                    {color.name_ru && (
                      <span className="truncate text-[11.5px] text-grey-400">{color.name_ru}</span>
                    )}
                  </div>
                  {/* Actions always visible — discoverable + touch-friendly. */}
                  <div className="flex flex-none items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setRenamingId(color.id)}
                      aria-label={t("catalogue.rename")}
                      title={t("catalogue.rename")}
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] text-grey-300 transition active:scale-95 hover:bg-grey-100 hover:text-grey-700"
                    >
                      <Pencil size={13.5} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(color)}
                      aria-label={t("catalogue.delete")}
                      title={t("catalogue.delete")}
                      className="flex h-7 w-7 items-center justify-center rounded-[6px] text-grey-300 transition active:scale-95 hover:bg-red-bg hover:text-warning"
                    >
                      <Trash2 size={13.5} strokeWidth={2} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
      </div>

      {/* Add color — bilingual: uz name (required) + optional ru name */}
      <div className="flex-none border-t border-grey-200 bg-grey-50 p-3">
        <ColorInlineAdd busy={createColor.isPending} onAdd={handleAdd} />
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title={t("catalogue.deleteColorTitle")}
        message={t("catalogue.deleteColorHint")}
        confirmLabel={t("catalogue.confirm")}
        cancelLabel={t("catalogue.cancel")}
        tone="warning"
        busy={deleteColor.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ── Main screen ────────────────────────────────────────────────────────────────

export function CatalogueScreen() {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<CatalogueTab>("car");
  const [searchInput, setSearchInput] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const debouncedSearch = useDebounce(searchInput, 200);

  // Brand/model data only fetched for car/technology tabs — not for colors.
  const isBrandTab = activeTab === "car" || activeTab === "technology";
  const catalogueType = isBrandTab ? (activeTab as LoanCatalogueType) : "car";

  const {
    data: brands,
    isLoading,
    isError,
  } = useCatalogueBrands(
    // Hook is always called (Rules of Hooks) but query enabled only for brand tabs.
    catalogueType,
  );
  const createBrand = useCreateBrand();

  const filteredBrands = useMemo(() => {
    if (!isBrandTab) return [];
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return brands ?? [];
    return (brands ?? []).filter((b) => b.name.toLowerCase().includes(q));
  }, [brands, debouncedSearch, isBrandTab]);

  // Keep a valid selection: pick the first visible brand whenever the current one
  // drops out of view (type switch, search, delete) — so the right pane is never
  // stale and searching a brand name "opens" the first match automatically.
  useEffect(() => {
    if (!isBrandTab || !brands) return;
    const stillVisible =
      selectedBrandId !== null && filteredBrands.some((b) => b.id === selectedBrandId);
    if (!stillVisible) setSelectedBrandId(filteredBrands[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, debouncedSearch, isBrandTab]);

  const selectedBrand = isBrandTab
    ? (brands?.find((b) => b.id === selectedBrandId) ?? null)
    : null;

  async function handleAddBrand(name: string): Promise<boolean> {
    try {
      const created = await createBrand.mutateAsync({ type: catalogueType, name });
      showToast(t("catalogue.saved"));
      // Jump straight to the brand the operator just created.
      if (created?.id) setSelectedBrandId(created.id);
      return true;
    } catch {
      showToast(t("catalogue.error"));
      return false;
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="mb-4 flex flex-none flex-wrap items-center gap-2.5">
        <TypeToggle
          value={activeTab}
          onChange={(next) => {
            setActiveTab(next);
            setSearchInput("");
            setSelectedBrandId(null);
          }}
        />
        {/* Search bar is only relevant for brand/model tabs */}
        {isBrandTab && (
          <div className="relative w-full min-w-[200px] sm:w-[280px]">
            {/* Leading-icon: pl-9 preserves room for the Search icon at left-3. */}
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-400"
            />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("catalogue.searchPlaceholder")}
              className="h-[38px] rounded-[9px] pl-9 pr-3 text-[13.5px]"
            />
          </div>
        )}
      </div>

      {/* Colors tab — flat list panel */}
      {activeTab === "colors" && <ColorPanel />}

      {/* Brand/model tabs — master-detail grid */}
      {isBrandTab && (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:h-[calc(100dvh-180px)] md:min-h-[460px] md:grid-cols-[clamp(240px,28%,320px)_1fr]">
          <BrandList
            type={catalogueType}
            brands={brands}
            filtered={filteredBrands}
            isLoading={isLoading}
            isError={isError}
            searching={debouncedSearch.trim().length > 0}
            selectedId={selectedBrandId}
            onSelect={setSelectedBrandId}
            onAddBrand={handleAddBrand}
            addBusy={createBrand.isPending}
          />

          {selectedBrand ? (
            <ModelDetail
              type={catalogueType}
              brand={selectedBrand}
              onBrandDeleted={() => setSelectedBrandId(null)}
            />
          ) : (
            <NoBrandSelected />
          )}
        </div>
      )}
    </div>
  );
}
